import React, { useState, useCallback } from 'react';
import {
  Paper,
  Typography,
  CircularProgress,
  Box,
  IconButton,
  TextField,
  Grid,
  InputAdornment,
  Button,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import DateRangeIcon from '@mui/icons-material/DateRange';

// API call to fetch closed transfer bond requests
const getClosedRequests = async (token, startDate, endDate) => {
  const baseURL = process.env.REACT_APP_BASE_URL;
  try {
    const params = new URLSearchParams({
      startDate,
      endDate,
    });

    const url = `${baseURL}requestingTransferBond/Closed?${params.toString()}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error fetching closed requests: ${errorText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching closed requests:', error);
    toast.error(error.message || 'Error fetching closed requests.');
    throw error;
  }
};

const ClosedRequestsGrid = ({ canViewPostingAgency }) => {
  const navigate = useNavigate();
  const [groupedRows, setGroupedRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchRef, setSearchRef] = useState('');
  const [startDate, setStartDate] = useState(
    new Date(new Date().setDate(new Date().getDate() - 30))
      .toISOString()
      .slice(0, 10)
  );
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));

  // Fetch the closed requests data and group by dbaAgencyName
  const fetchClosedRequests = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('jwtToken');
    if (!token) {
      toast.error('No authentication token found.');
      setLoading(false);
      return;
    }

    try {
      // Validate date range (maximum 90 days)
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffInDays = (end - start) / (1000 * 60 * 60 * 24);

      if (diffInDays > 90) {
        toast.error('Date range should not exceed 90 days.');
        setLoading(false);
        return;
      }

      if (start > end) {
        toast.error('Start date cannot be after end date.');
        setLoading(false);
        return;
      }

      const data = await getClosedRequests(token, startDate, endDate);

      // Group the data by dbaAgencyName
      const groupedData = data.reduce((acc, request) => {
        let agencyGroup = acc.find(
          (group) => group.dbaAgencyName === request.dbaAgencyName
        );
        if (!agencyGroup) {
          agencyGroup = {
            dbaAgencyName: request.dbaAgencyName,
            records: [],
          };
          acc.push(agencyGroup);
        }
        const dateObj = new Date(request.createdAt);
        const dateStr = dateObj.toLocaleDateString('en-US');
        const timeStr = dateObj.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

        agencyGroup.records.push({
          id: request.transferBondTransactionID,
          defendantName: `${request.defendantFirstName || ''} ${request.defendantLastName || ''}`.trim() || 'N/A',
          refNumber: request.transferBondTransactionID.toString(),
          createdAt: new Date(request.createdAt),
          createdAtFormatted: { date: dateStr, time: timeStr },
          numberOfBonds: request.numberOfBonds,
          totalBondAmount: `$${request.totalBondAmount.toLocaleString()}`,
          county: request.county,
          state: request.state,
          isOnline: request.isOnline,
          postingAgencyName: request.postingAgencyName || '-',
        });
        return acc;
      }, []);

      setGroupedRows(groupedData);
    } catch (error) {
      // Error handled in getClosedRequests
    } finally {
      setLoading(false); // Stop loading when data is fetched or error occurs
    }
  }, [startDate, endDate]);

  const handleViewDetails = useCallback(
    (id) => {
      navigate(`/view-details?ID=${id}`);
    },
    [navigate]
  );

  // Client-side filtering based on reference number
  const getFilteredGroups = () => {
    let filteredData = groupedRows.map((group) => {
      let records = group.records;

      // Filter by reference number
      if (searchRef.trim() !== '') {
        records = records.filter((record) =>
          record.refNumber.includes(searchRef.trim())
        );
      }

      return {
        ...group,
        records,
      };
    });

    // Remove groups with no records after filtering
    filteredData = filteredData.filter((group) => group.records.length > 0);

    return filteredData;
  };

  const columns = [
    {
      field: 'refNumber',
      headerName: 'Ref #',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          height: '100%',
          width: '100%'
        }}>
          <Typography variant="body2">{params.value}</Typography>
          <IconButton
            color="primary"
            size="small"
            onClick={() => handleViewDetails(params.row.id)}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
    {
      field: 'defendantName',
      headerName: 'Defendant Name',
      width: 200,
      align: 'center',
      headerAlign: 'center',
    },
    ...(canViewPostingAgency
      ? [
          {
            field: 'postingAgencyName',
            headerName: 'Posting Agency',
            width: 200,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
              <Typography
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                  fontSize: '0.85rem',
                  lineHeight: 1.4,
                  height: '100%',
                }}
              >
                {params.row.postingAgencyName || '-'}
              </Typography>
            ),
          },
        ]
      : []),
    {
      field: 'createdAtFormatted',
      headerName: 'Created At',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2">{params.value.date}</Typography>
          <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
            {params.value.time}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'origination',
      headerName: 'Origin',
      width: 175,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        if (params.row.isOnline === true) {
          return <span style={{ fontStyle: 'italic' }}>Online</span>;
        }
        return <span style={{ fontStyle: 'italic' }}>Bankers In-House</span>;
      },
    },
    {
      field: 'totalBondAmount',
      headerName: 'Total Bond Amount',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => `$${params.value?.toLocaleString() || '0'}`
    },
    {
      field: 'county',
      headerName: 'County',
      width: 100,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'state',
      headerName: 'State',
      width: 80,
      align: 'center',
      headerAlign: 'center',
    },
  ];

  const handleSearch = () => {
    fetchClosedRequests();
  };

  return (
    <Paper sx={{ padding: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Closed Requests
      </Typography>

      {/* Instructions */}
      <Typography variant="body2" sx={{ mb: 2 }}>
        • You can search closed requests within a 90-day date range.
        <br />
        • Use the "Filter by Reference #" input to filter the grid results.
      </Typography>

      {/* Date Range Filters */}
      <Box sx={{ mt: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Date Range Inputs */}
          <Grid item xs={12} sm={4}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DateRangeIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DateRangeIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          {/* Search Button */}
          <Grid item xs={12} sm={4}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleSearch}
              disabled={loading}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Reference Number Filter */}
      <Box sx={{ mt: 2, mb: 2 }}>
        <TextField
          label="Filter by Reference #"
          value={searchRef}
          onChange={(e) => setSearchRef(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSearch();
            }
          }}
          fullWidth
          placeholder="Type to filter displayed results..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="caption" color="textSecondary">
          This filters the displayed data based on the reference number.
        </Typography>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
          }}
        >
          <CircularProgress />
        </Box>
      ) : getFilteredGroups().length > 0 ? (
        getFilteredGroups().map((agencyGroup, index) => (
          <Box key={index} sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontSize: '1.1rem', mt: 2 }}>
              {agencyGroup.dbaAgencyName}
            </Typography>
            <Paper sx={{ width: '100%' }}>
              <DataGrid
                rows={agencyGroup.records}
                columns={columns}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 10 },
                  },
                }}
                pageSizeOptions={[10]}
                pagination
                autoHeight
                sx={{ fontSize: '0.85rem' }}
              />
            </Paper>
          </Box>
        ))
      ) : (
        // No records after filtering
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
          }}
        >
          <Typography variant="body1" color="textSecondary">
            No closed requests found.
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default ClosedRequestsGrid;
